import { useCallback, useState } from 'react'
import type { PressableProps } from 'react-native'

import * as Comp from './item.styles'

import { Icon } from '~/components/icon'

import type { IconName } from '@raiadrogasil/pulso-icons'

import { useMapVariantsToState } from '../../hooks/use-map-variants-to-state'

export interface ItemProps
  extends Pick<PressableProps, 'onPress' | 'disabled'> {
  type?: 'single' | 'multi'
  content: {
    prefixIcon?: IconName
    label: string
    description?: string
  }
  checked?: boolean
  testID?: string
}

export function Item(props: Omit<ItemProps, 'type'>) {
  const {
    type = 'single',
    content,
    checked,
    disabled,
    testID,
    ...rest
  } = props as ItemProps

  const [hasPressed, setHasPressed] = useState(false)

  const hasPrefixIcon = !!content.prefixIcon?.length
  const hasDescription = !!content.description?.length

  const isDisabled = !!disabled

  const { checkboxSymbol, checkboxColor, prefixIconColor, checkColor } =
    useMapVariantsToState(isDisabled, checked)

  const handlePress = useCallback((type: 'in' | 'out') => {
    setHasPressed(type === 'in')
  }, [])

  return (
    <Comp.Container
      {...rest}
      onPressIn={() => handlePress('in')}
      onPressOut={() => handlePress('out')}
      disabled={isDisabled}
      testID={testID}
    >
      <Comp.Wrapper
        type={type}
        hasPressed={hasPressed}
        hasDisabled={isDisabled}
      >
        {type === 'multi' && (
          <Comp.CheckboxContainer>
            <Icon symbol={checkboxSymbol} color={checkboxColor} size="small" />
          </Comp.CheckboxContainer>
        )}

        {hasPrefixIcon && (
          <Icon
            symbol={content.prefixIcon}
            color={prefixIconColor}
            size="small"
          />
        )}

        <Comp.Content>
          <Comp.Label hasDisabled={isDisabled}>{content.label}</Comp.Label>

          {hasDescription && (
            <Comp.Description
              hasDisabled={isDisabled}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {content.description}
            </Comp.Description>
          )}
        </Comp.Content>

        {type === 'single' && checked ? (
          <Icon symbol="rdicon-checkmark" color={checkColor} size="small" />
        ) : null}
      </Comp.Wrapper>
    </Comp.Container>
  )
}
